import { filter, find, forEach, isEmpty, omit } from 'lodash';
import { duplicateQuestion as duplicateQuestionHelper } from 'containers/QuestionnairePage/utils';
import { transformPropsDeep } from 'containers/App/utils';
import { SCREENING_ALLOWED_VALUES } from 'components/ScreeningQuestion/constants';
import {
  SURVEY_TYPE_PEEQ,
  SURVEY_TYPE_DISTRIBUTION_LINK,
  PEEQ_CATEGORY_TYPE_PREPAID,
  PEEQ_CATEGORY_TYPE_INVOICED,
} from 'containers/Survey/constants';

export function screeningQuestionToNormal(screeningQuestion) {
  let clonedQuestion = duplicateQuestionHelper(screeningQuestion);
  clonedQuestion = transformPropsDeep(clonedQuestion, { isScreening: false });
  clonedQuestion = omit(clonedQuestion, ['hasScreeningQuotas']);
  clonedQuestion.answers = clonedQuestion.answers.map((answer) =>
    omit(answer, ['screening', 'screenerType']),
  );
  return clonedQuestion;
}

export function screeningQuestionToScreeningWithScreeningQL(screeningQuestion) {
  let clonedQuestion = duplicateQuestionHelper(screeningQuestion);
  clonedQuestion.isScreenerActive = screeningQuestion.isScreening || undefined;
  clonedQuestion = omit(clonedQuestion, ['hasScreeningQuotas']);
  clonedQuestion.answers = clonedQuestion.answers.map((answer) =>
    omit(answer, ['screening', 'screenerType']),
  );
  return clonedQuestion;
}

/**
 * Adds a copy of a template screening question to the questionnaire as a
 * screening question and returns it. It is added with every answer allowed;
 * the template's other screener types (kept on its answers for the targeting
 * page) are then set through the questionnaire's `setAnswerScreenerType`
 * handler, since the screening rules are their only record.
 */
export function addScreeningQuestionWithScreeningQL(Survey, screeningQuestion) {
  const question =
    screeningQuestionToScreeningWithScreeningQL(screeningQuestion);
  Survey.questionnaire.addQuestion(question);

  question.answers.forEach(({ refId }, index) => {
    const screenerType = screeningQuestion.answers[index]?.screenerType;
    if (screenerType && screenerType !== SCREENING_ALLOWED_VALUES.ALLOWED) {
      Survey.questionnaire.setAnswerScreenerType({
        questionRefId: question.refId,
        answerRefId: refId,
        screenerType,
      });
    }
  });

  return question;
}

export function updateCarryForwardOnNode(nodeToUpdate, newNodeRefId) {
  const { carryForward = [] } = nodeToUpdate;
  return {
    ...nodeToUpdate,
    // we need to clear the carriedForward objects so that the updateQuestion
    // will handle adding the proper answers from the carryForward configuration
    answers: filter(nodeToUpdate.answers, ['isCarriedForward', false]),
    scorepoints: filter(nodeToUpdate.scorepoints, ['isCarriedForward', false]),
    carryForward: carryForward.map((cf) => ({
      ...cf,
      questionRefId: newNodeRefId,
    })),
  };
}

export function repointCarryForwardFromScreening(
  Survey,
  newScreenerRefId,
  oldScreeningRefId,
) {
  forEach(
    Survey.questionnaire.state.surveyDetailsExtended.questions,
    (question) => {
      const { carryForward = [] } = question;
      if (isEmpty(carryForward)) {
        return;
      }

      const referencesScreening = carryForward.some(
        ({ questionRefId }) => questionRefId === oldScreeningRefId,
      );
      if (!referencesScreening) {
        return;
      }

      Survey.questionnaire.updateQuestion(
        updateCarryForwardOnNode(question, newScreenerRefId),
      );
    },
  );
}

export function shouldUseScreeningQl({ surveyType, peeqCategoryType }) {
  return (
    surveyType === SURVEY_TYPE_PEEQ &&
    peeqCategoryType === PEEQ_CATEGORY_TYPE_PREPAID
  );
}

export function shouldConvertScreeningToQlQuestions({
  surveyType,
  peeqCategoryType,
}) {
  return (
    surveyType === SURVEY_TYPE_DISTRIBUTION_LINK ||
    (surveyType === SURVEY_TYPE_PEEQ &&
      peeqCategoryType === PEEQ_CATEGORY_TYPE_INVOICED)
  );
}

/**
 * After convert-to-ADL updates (CF, recall, ADL, etc.), remove original
 * screening node(s) and place converted screener(s) as an ordered prefix.
 * Call only once those content updates that may still reference originals
 * have finished.
 */
export function finalizeConvertedScreeningPlacement(Survey, placements) {
  const questions = () =>
    Survey.questionnaire.state.surveyDetailsExtended.questions || [];

  placements.forEach(({ originalRefId }) => {
    if (questions().some((question) => question.refId === originalRefId)) {
      Survey.questionnaire.removeQuestion({ refId: originalRefId });
    }
  });

  Survey.questionnaire.removeAllScreeningQuestions();

  placements.forEach(({ convertedQuestion }, targetIndex) => {
    const currentIndex = questions().findIndex(
      (question) => question.refId === convertedQuestion.refId,
    );
    if (currentIndex === -1 || currentIndex === targetIndex) {
      return;
    }
    Survey.questionnaire.moveQuestion(
      convertedQuestion,
      targetIndex - currentIndex,
    );
  });
}

/**
 * Apply screening template edits through the correct questionnaire API.
 * Peeq / screening-logic-enabled: merge onto the live questions[] node and
 * call updateQuestion so isScreening (and other live metadata) are preserved.
 * Classic: keep the legacy updateScreeningQuestion path.
 */
export function applyScreeningQuestionContentUpdate(
  Survey,
  templateEdits,
  { surveyType, peeqCategoryType },
) {
  if (shouldUseScreeningQl({ surveyType, peeqCategoryType })) {
    const questions =
      Survey.questionnaire.state.surveyDetailsExtended.questions || [];
    const liveNode = find(questions, { refId: templateEdits.refId });
    // Before Peeq conversion the screening node is not in questions[] yet;
    // callers apply edits after addQuestion when the live node exists.
    if (!liveNode) {
      return;
    }
    Survey.questionnaire.updateQuestion({
      ...liveNode,
      ...templateEdits,
      isScreening: liveNode.isScreening,
    });
    return;
  }

  Survey.questionnaire.updateScreeningQuestion(templateEdits);
}
